import React, { useState, useEffect } from "react";
import { DeviceMobile, CaretRight, ShareNetwork, Plus, X, AndroidLogo, AppleLogo } from "@phosphor-icons/react";

function detectPlatform() {
  if (typeof window === "undefined") return { os: "unknown", isStandalone: false };
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (isStandalone) return { os: "installed", isStandalone };
  if (isIOS) return { os: "ios", isStandalone };
  if (isAndroid) return { os: "android", isStandalone };
  return { os: "desktop", isStandalone };
}

export default function AddToHomeButton() {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState({ os: "unknown", isStandalone: false });

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  // Hide button entirely if already running as installed PWA
  if (platform.os === "installed") return null;

  const label =
    platform.os === "ios"
      ? "Add Tally to your iPhone"
      : platform.os === "android"
      ? "Add Tally to your Android"
      : "Install Tally on this device";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors text-left"
        data-testid="menu-install"
      >
        <div className="flex items-center gap-3">
          <DeviceMobile size={18} className="text-[#5C6B62]" />
          <span className="text-sm text-[#1A2421]">{label}</span>
        </div>
        <CaretRight size={14} className="text-[#5C6B62]" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#1A2421]/45 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-4"
          data-testid="install-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-[#F9F8F6] p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-[#5C6B62] hover:text-[#1A2421]"
              aria-label="Close"
              data-testid="install-modal-close"
            >
              <X size={18} />
            </button>

            <div className="flex justify-center pt-2">
              <span className="w-14 h-14 rounded-full bg-[#2B422F] flex items-center justify-center">
                <DeviceMobile size={24} weight="regular" className="text-[#E6D4C4]" />
              </span>
            </div>

            <div className="mt-5 text-center">
              <span className="label-eyebrow">Get the app feel</span>
              <h2 className="font-serif-display text-2xl text-[#1A2421] mt-2 tracking-tight">
                Add Tally to your home screen
              </h2>
              <p className="text-sm text-[#5C6B62] mt-2">
                Opens like an app, no App Store needed.
              </p>
            </div>

            {platform.os === "ios" && (
              <ol className="mt-6 space-y-3 text-sm text-[#1A2421]" data-testid="install-steps-ios">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#FFD9B7] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">1</span>
                  <span>
                    Open <strong>tallycarbcounter.com</strong> in <strong>Safari</strong>
                    <span className="block text-xs text-[#5C6B62] mt-0.5">(not Chrome or Google) </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#FFD9B7] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">2</span>
                  <span>
                    Tap the <ShareNetwork size={14} className="inline mb-0.5" weight="bold" /> <strong>Share</strong> button at the bottom of Safari
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#FFD9B7] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">3</span>
                  <span>
                    Scroll down and tap <Plus size={14} className="inline mb-0.5" weight="bold" /> <strong>Add to Home Screen</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#FFD9B7] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">4</span>
                  <span>Tap <strong>Add</strong> in the top-right. Done!</span>
                </li>
              </ol>
            )}

            {platform.os === "android" && (
              <ol className="mt-6 space-y-3 text-sm text-[#1A2421]" data-testid="install-steps-android">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#BFE3C9] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">1</span>
                  <span>
                    Open <strong>tallycarbcounter.com</strong> in <strong>Chrome</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#BFE3C9] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">2</span>
                  <span>Tap the <strong>⋮ three-dot menu</strong> (top-right of Chrome)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#BFE3C9] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">3</span>
                  <span>Tap <strong>Add to Home screen</strong> (or <strong>Install app</strong>)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#BFE3C9] text-[#1A2421] text-xs font-bold flex items-center justify-center shrink-0">4</span>
                  <span>Tap <strong>Install</strong>. Done!</span>
                </li>
              </ol>
            )}

            {(platform.os === "desktop" || platform.os === "unknown") && (
              <div className="mt-6 space-y-4 text-sm text-[#1A2421]">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white border border-[#E6E3D8]">
                  <AppleLogo size={22} weight="fill" className="text-[#1A2421] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">iPhone / iPad</div>
                    <div className="text-xs text-[#5C6B62] mt-1">
                      Open in <strong>Safari</strong> → tap <strong>Share</strong> → <strong>Add to Home Screen</strong>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white border border-[#E6E3D8]">
                  <AndroidLogo size={22} weight="fill" className="text-[#5BAE7B] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Android</div>
                    <div className="text-xs text-[#5C6B62] mt-1">
                      Open in <strong>Chrome</strong> → tap <strong>⋮ menu</strong> → <strong>Add to Home screen</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setOpen(false)}
              className="btn-primary w-full mt-6"
              data-testid="install-modal-done"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
